import React, { Suspense, useCallback } from 'react';

import { SmartCardProvider } from '@atlaskit/link-provider';
import { Inline } from '@atlaskit/primitives/compiled';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { getSearchAIAnalyticsContextData, SearchAIAnalyticsContextLocation } from '../../../ai-answer-dialog/analytics';
import { onAIAnswerSourceClick } from '../../../ai-answer-dialog/on-ai-answer-source-click';
import { SearchAIAnalyticsContext } from '../../../ai-answer-dialog/search-ai-analytics-context';
import type { NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics/hooks/use-sain-usage';
import { SourceCard } from '../../../common/ui/ai-sources/source-card';

export function AITabsSources(): React.JSX.Element {
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { callbacks, commonAttributes, fireAnalyticsEvent } = useAIAnswerContext();
	const { searchSessionId, sources } = commonAttributes;

	const sourceClickCallback = useCallback(
		(index: number, source: NLPSearchResultSource, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					sourceProduct: source.product || '',
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct: source.product || '',
			});
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, callbacks, fireSAINUsageEvent],
	);

	return (
		<Suspense fallback={<></>}>
			<SearchAIAnalyticsContext
				data={getSearchAIAnalyticsContextData({
					location: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
					commonAttributes,
				})}
			>
				<SmartCardProvider>
					<Inline alignBlock="center" rowSpace="space.100" shouldWrap={true} space="space.050">
						{sources.map((source, index) => (
							<SourceCard
								index={index}
								key={`search-ai-answer-source-${index}`}
								source={source}
								searchSessionId={searchSessionId}
								onClickHandler={sourceClickCallback}
								enableSmartLinkSources={true}
							/>
						))}
					</Inline>
				</SmartCardProvider>
			</SearchAIAnalyticsContext>
		</Suspense>
	);
}
